import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import {
  completeInvitePassword,
  confirmForgotPassword,
  confirmSignUp,
  forgotPassword,
  resendConfirmationCode,
  signIn,
  signUp,
} from '../api/auth-api';
import {
  AUTH_EXPIRED_FLASH_MESSAGE_KEY,
  AUTH_SESSION_QUERY_KEY,
} from '../constants';
import { writeStoredSession } from '../lib/authSessionStorage';
import type {
  ActionResult,
  ApiErrorResponse,
  ApiResponse,
  CompleteInvitePasswordRequest,
  ConfirmForgotPasswordRequest,
  ConfirmSignUpRequest,
  ForgotPasswordRequest,
  ResendConfirmationRequest,
  SignInRequest,
  SignInResponse,
  SignUpRequest,
  SignUpResponse,
} from '../types/auth';

type ValidationErrorBody = {
  message?: string;
  error?: {
    message?: string;
    details?: { path?: string; message: string }[];
  };
};

export function getApiErrorMessage(error: unknown): string {
  if (error instanceof AxiosError) {
    const body = error.response?.data as ValidationErrorBody | undefined;
    const details = body?.error?.details;
    if (details?.length) {
      return details.map((d) => d.message).join(' ');
    }
    return (
      body?.error?.message ??
      body?.message ??
      error.message ??
      'Something went wrong. Please try again.'
    );
  }

  if (error instanceof Error) {
    return error.message;
  }

  return 'Something went wrong. Please try again.';
}

export function useSignInMutation() {
  const queryClient = useQueryClient();

  return useMutation<
    SignInResponse,
    AxiosError<ApiErrorResponse>,
    SignInRequest
  >({
    mutationKey: ['auth', 'sign-in'],
    mutationFn: signIn,
    onSuccess: (data) => {
      if (data.success && data.data?.accessToken) {
        try {
          sessionStorage.removeItem(AUTH_EXPIRED_FLASH_MESSAGE_KEY);
        } catch {
          // ignore
        }
        queryClient.setQueryData(AUTH_SESSION_QUERY_KEY, data);
        writeStoredSession(data);
      }
    },
  });
}

export function useSignUpMutation() {
  return useMutation<
    SignUpResponse,
    AxiosError<ApiErrorResponse>,
    SignUpRequest
  >({
    mutationKey: ['auth', 'sign-up'],
    mutationFn: signUp,
  });
}

export function useConfirmSignUpMutation() {
  return useMutation<
    ApiResponse<ActionResult>,
    AxiosError<ApiErrorResponse>,
    ConfirmSignUpRequest
  >({
    mutationKey: ['auth', 'confirm-sign-up'],
    mutationFn: confirmSignUp,
  });
}

export function useResendConfirmationMutation() {
  return useMutation<
    ApiResponse<ActionResult>,
    AxiosError<ApiErrorResponse>,
    ResendConfirmationRequest
  >({
    mutationKey: ['auth', 'resend-confirmation'],
    mutationFn: resendConfirmationCode,
  });
}

export function useForgotPasswordMutation() {
  return useMutation<
    ApiResponse<ActionResult>,
    AxiosError<ApiErrorResponse>,
    ForgotPasswordRequest
  >({
    mutationKey: ['auth', 'forgot-password'],
    mutationFn: forgotPassword,
  });
}

export function useConfirmForgotPasswordMutation() {
  return useMutation<
    ApiResponse<ActionResult>,
    AxiosError<ApiErrorResponse>,
    ConfirmForgotPasswordRequest
  >({
    mutationKey: ['auth', 'confirm-forgot-password'],
    mutationFn: confirmForgotPassword,
  });
}

export function useCompleteInvitePasswordMutation() {
  const queryClient = useQueryClient();

  return useMutation<
    SignInResponse,
    AxiosError<ApiErrorResponse>,
    CompleteInvitePasswordRequest
  >({
    mutationKey: ['auth', 'complete-invite-password'],
    mutationFn: completeInvitePassword,
    onSuccess: (data) => {
      if (data.success && data.data?.accessToken) {
        try {
          sessionStorage.removeItem(AUTH_EXPIRED_FLASH_MESSAGE_KEY);
        } catch {
          // ignore
        }
        queryClient.setQueryData(AUTH_SESSION_QUERY_KEY, data);
        writeStoredSession(data);
      }
    },
  });
}
